/**
 * Line that `formatMarkdown` turns into a list item. Its list regex runs to the
 * next blank line, and lists separated by a single blank line are then merged.
 */
const LIST_ITEM_LINE = /^ *(?:[*\-+]|\d+[.)]|[a-zA-Z][.)]) +/;
const LIST_ITEM_ANYWHERE = /(?:^|\n) *(?:[*\-+]|\d+[.)]|[a-zA-Z][.)]) +/;
/** Text that could still grow into a {@link LIST_ITEM_LINE}. */
const LIST_ITEM_PREFIX = /^ *(?:[*\-+]|\d+[.)]?|[a-zA-Z][.)]?)?$/;
const FENCE = /^(```|~~~)/;

/**
 * A block from {@link splitSettledMarkdown}, ready for `formatMarkdown`. Blank
 * lines at either end only separate it from its neighbours, and rendered on
 * their own they become an empty paragraph.
 */
export function trimBlankLines(block: string): string {
	return block.replace(/^\n+|\n+$/g, "");
}

/**
 * Splits streamed markdown into leading blocks whose `formatMarkdown` output can
 * no longer change, and the trailing text that may still change meaning as more
 * arrives. Rendering each settled block on its own produces the same elements
 * as rendering the whole text, so they can be rendered once and kept.
 *
 * Blocks end at a blank line, except inside a code fence (which spans blank
 * lines until it closes) and between list items (which `formatMarkdown` joins
 * into one list across a single blank line). The blank line between blocks
 * opens the next one, so a block settles as soon as it arrives. `settled`
 * concatenated with `tail` is always `markdown`.
 */
export function splitSettledMarkdown(markdown: string): {
	settled: string[];
	tail: string;
} {
	const settled: string[] = [];
	let blockStart = 0;
	let lineStart = 0;

	while (true) {
		while (markdown[lineStart] === "\n") {
			lineStart++;
		}
		if (lineStart >= markdown.length) {
			break;
		}

		const atLineStart = lineStart === 0 || markdown[lineStart - 1] === "\n";
		const fence = atLineStart
			? markdown.slice(lineStart).match(FENCE)?.[1]
			: undefined;
		if (fence) {
			// `formatMarkdown` closes a fence at the next occurrence of its marker,
			// even mid-line, so blank lines up to there belong to this block.
			const openingLineEnd = markdown.indexOf("\n", lineStart);
			const closing =
				openingLineEnd === -1 ? -1 : markdown.indexOf(fence, openingLineEnd);
			if (closing === -1) {
				break;
			}
			lineStart = closing + fence.length;
		}

		const boundary = markdown.indexOf("\n\n", lineStart);
		if (boundary === -1) {
			break;
		}

		const nextFence = markdown.slice(lineStart, boundary).search(/\n(```|~~~)/);
		if (nextFence !== -1) {
			lineStart += nextFence + 1;
			continue;
		}

		const block = markdown.slice(blockStart, boundary);
		if (LIST_ITEM_ANYWHERE.test(block)) {
			const next = markdown.slice(boundary + 2);
			if (LIST_ITEM_LINE.test(next)) {
				lineStart = boundary + 2;
				continue;
			}
			if (!next.includes("\n") && LIST_ITEM_PREFIX.test(next)) {
				break;
			}
		}

		settled.push(block);
		blockStart = boundary;
		lineStart = boundary;
	}

	return { settled, tail: markdown.slice(blockStart) };
}
